import clsx from "clsx";
import Form from "components/Reusable/Form/Form";
import config from "config";
import Recaptcha from "../../../Recaptcha/Recaptcha";

import { memo, useRef } from "react";
import { useContent } from "contexts/contentContext/contentContext";
import { RECAPTCHA_APPEAR_DELAY } from "utils/globalConsts";
import useCustomLoginErrorHandler from "hooks/useCustomLoginErrorHandler";
import { useUser } from "contexts/userContext/userContext";
import { useModal } from "contexts/modalContext/modalContext";
import { useToasts } from "react-toast-notifications";
import { useParams } from "react-router-dom";
import { awaiter } from "components/Pages/Home/Helpers/helpers";
import { signupFields } from "./helpers/signUpFields";
import { CustomDomainPagesE } from "models/enums";

import styles from "./signUpForm.module.scss";

interface SignUpFormI extends React.HTMLAttributes<HTMLDivElement> {
  email?: string;
  autoConfirm?: boolean;
  onSubmitSuccess?: (...args: unknown[]) => void;
  onSubUserSubmitSuccess?: (email: string) => void;
}

const isInviteByLinkPage = window.location.pathname.includes(
  CustomDomainPagesE.INVITE
);
const isInviteByEmailPage = window.location.pathname.includes(
  CustomDomainPagesE.SUB_USER_COMPLETE
);

const SignUpForm = memo((props: SignUpFormI) => {
  const {
    email,
    autoConfirm,
    onSubmitSuccess,
    onSubUserSubmitSuccess,
    className,
    ...restProps
  } = props;

  const params = useParams();
  const { getTextByKey, getPath } = useContent();
  const { signup, login } = useUser();
  const { closeModal } = useModal();
  const { addToast } = useToasts();

  const handleCustomError = useCustomLoginErrorHandler();

  const externalRecaptchaRef = useRef(null);

  const handleCallRecaptcha = async () => {
    if (!externalRecaptchaRef.current) {
      console.error("Recaptcha reference is not available");
      return null;
    }

    try {
      // externalRecaptchaRef.current.reset?.();
      const token = await externalRecaptchaRef.current?.execute();
      return token;
    } catch (error) {
      console.error("Error executing recaptcha:", error);
      return null;
    }
  };

  const handleSubmit = async (body) => {
    if (params.UserInvitationToken !== undefined) {
      body = {
        ...body,
        RegistrationPage: "react",
        userInvitationToken: params.UserInvitationToken,
      };
    } else {
      body = {
        ...body,
        RegistrationPage: "react",
      };
    }

    if (autoConfirm) {
      body = {
        ...body,
        AutoConfirm: true,
      };
    }

    const response = await signup(body);

    if (response.status === "success") {
      addToast(getTextByKey("sign-up-success"), { appearance: "success" });
      if (onSubmitSuccess) {
        localStorage.setItem("proccessSignUpEmbedPage", true.toString());
      }

      await awaiter(RECAPTCHA_APPEAR_DELAY);

      const recaptchaResponse = await handleCallRecaptcha();

      const loginRes = await login(
        {
          Password: body.Password,
          Remember: false,
          UserName: body.Email,
          recaptchaResponse: recaptchaResponse,
          captchaServiceName: body.captchaServiceName,
        },
        handleCallRecaptcha
      );

      if (loginRes.status === "success") {
        if (onSubUserSubmitSuccess && isInviteByLinkPage) {
          await onSubUserSubmitSuccess(body.Email);
        }

        if (onSubUserSubmitSuccess && isInviteByEmailPage) {
          await onSubUserSubmitSuccess(body.Email);
          closeModal();
          return;
        }

        if (onSubmitSuccess) {
          const response = { ...loginRes.message, IsRegistered: true };
          onSubmitSuccess(response);
        } else {
          window.location.href =
            config.baseurl + getPath() + "registration-complete";
        }
      }
    }
    if (response.status === "error") {
      handleCustomError(response.message);
    }

    return response;
  };

  return (
    <>
      <Recaptcha recaptchaRef={externalRecaptchaRef} />
      <Form
        //@ts-ignore
        fieldsProps={{ label: "hidden", field: "square" }}
        buttonProps={{ buttonsSize: "large" }}
        fields={signupFields}
        onSubmit={handleSubmit}
        defaultValuesObj={{ Email: email || "" }}
        submitProps={{ title: getTextByKey("create-free-account") }}
        className={clsx(styles.wrapper, className)}
        {...restProps}
      />
    </>
  );
});

export default SignUpForm;
